
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BehaviorSubject, from, Observable, Subject} from 'rxjs';
import {HttpService} from '../http-service.service';
import {ApiConst} from '../api-const';

/**
 * Handle all the Password part (change, ask for a new password, creation)
 */
@Injectable()
export class PasswordService {

  /**
   * if the back-office answer with an error Message, it will be store in errorMessage
   */
  public errorMessage: Subject<string>;

  /**
   * instantiate variable
   *
   * @param {HttpService} httpService - Service that will handle http request
   */
  constructor(private httpService: HttpService) {
    this.errorMessage = new Subject<string>();
  }

  private sha1Encrypt(data: string): Observable<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashObservable = from(crypto.subtle.digest('SHA-1', dataBuffer));
    return hashObservable.pipe(
      map(res => {
        let sUserPwd = btoa(String.fromCharCode.apply(null, new Uint8Array(res)));
        if (sUserPwd.endsWith('=')) {
          sUserPwd = sUserPwd.slice(0, -1);
        }
        return sUserPwd;
      })
    );
  }

  /**
   *
   * change the password, it wont check the validity of it but the back-office should.
   * If there's an error, it'll be store in the errorMessage
   *
   * @param {string} actualPassword - the actual password of the connected user
   * @param {string} newPassword - the new password
   * @param {string} newPasswordConfirm - the confirmation password that the user typed
   * @returns {Observable<boolean>} - true if the change succeed else false
   */
  public changePassword(actualPassword: string, newPassword: string, newPasswordConfirm: string): Observable<boolean> {
    const resultSubject = new BehaviorSubject<boolean>(false);
    this.sha1Encrypt(actualPassword).subscribe(hashedActualPassword => {
      const datasToSend = JSON.stringify({
        password: hashedActualPassword,
        newPassword: newPassword,
        newPasswordConfirm: newPasswordConfirm
      });

      this.httpService.$post(ApiConst.changePasswordRequest, datasToSend).pipe(
        map((response: any) => {
          const errorMsg = response.body && response.body.sMsgError;
          if (errorMsg) {
            this.errorMessage.next(errorMsg);
            resultSubject.next(false);
          } else {
            resultSubject.next(true);
          }
        })
      ).subscribe();
    });

    return resultSubject.asObservable();
  }

  /**
   *
   * handle the reset of a password (forgotten password)
   * If there's an error, it'll be store in the errorMessage
   *
   * @param {string}  idCustomer - customer that request a new password
   * @param {string}  idUser - user that request a new password
   * @param {string}  email - the email of the user that request a new password (here for verification purposes)
   * @returns {Observable<boolean>} - true if the reset succeed else false
   */
  public askResetPassword(idCustomer: string, idUser: string, email: string): Observable<boolean> {
    const datasToSend = JSON.stringify({
      customerID: idCustomer,
      userID: idUser,
      email: email
    });

    return this.httpService.$post(ApiConst.forgottenPasswordRequest, datasToSend).pipe(map((response: any) => {
      const errorMsg = response.body && response.body.sMsgError;
      if (errorMsg) {
        this.errorMessage.next(errorMsg);
        return false;
      }
      else {
        return true;
      }
    }));
  }

  /**
   *
   * handle the second part of forgotten password, when the reset succeeded
   * If there's an error, it'll be store in the errorMessage
   *
   * @param {string} newPassword - the new password
   * @param {string} newPasswordConfirm - the confirmation password that the user typed
   * @param {string} token - the token gave by the user (currently in the e-mail)
   * @returns {Observable<boolean>} - true if the creation succeed else false
   */
  public createNewPassword(newPassword: string, newPasswordConfirm: string, token: string): Observable<boolean> {
    const datasToSend = JSON.stringify({
      newPassword: newPassword,
      newPasswordConfirm: newPasswordConfirm,
      token: token
    });

    return this.httpService.$post(ApiConst.resetUserPasswordRequest, datasToSend).pipe(map((response: any) => {

      const errorMsg = response.body && response.body.sMsgError;
      if (errorMsg) {
        this.errorMessage.next(errorMsg);
        return false;
      }
      else {
        return true;
      }
    }));
  }
}
